export type RankItem = {
  rank: number;
  title: string;
  tag: string;
  heat: string;
  delta: string;
  up: boolean;
};

export type TopicGroup = {
  id: string;
  bucket: string;
  blurb: string;
  tags: string[];
};

export type FeatureItem = {
  icon: string;
  title: string;
  desc: string;
  kpi: string;
  accent?: boolean;
};

export type ArchiveItem = {
  id: string;
  title: string;
  lead: string;
  meta: string;
  heat: string;
  status: string;
  img: string;
};

export type TimelineStep = {
  time: string;
  title: string;
  desc: string;
  type: string;
};

export type RelationNode = {
  role: string;
  name: string;
  note: string;
  letter: string;
};

export const navLinks = [
  { label: "热度榜", key: "rank" },
  { label: "话题墙", key: "topics" },
  { label: "追踪维度", key: "features" },
  { label: "事件档案", key: "archives" },
  { label: "进展时间线", key: "timeline" },
  { label: "关联人物", key: "relations" },
];

export const heroStats = [
  { value: "1.2万+", label: "累计梳理事件" },
  { value: "8 类", label: "追踪覆盖维度" },
  { value: "15 分钟", label: "热度榜更新周期" },
  { value: "4.7万条", label: "关联脉络索引" },
];

export const heroQuickTags = [
  "网曝当事人是谁",
  "网曝身份起底",
  "网曝背景深扒",
  "网曝关联人物",
  "网曝社交账号查找",
  "网曝旧料翻出",
];

export const hotRank: RankItem[] = [
  { rank: 1, title: "「实名举报」后当事人身份疑云仍在发酵", tag: "当事人是谁", heat: "982万", delta: "+32.6%", up: true },
  { rank: 2, title: "某公司中层被点名：履历与公开职务线待核实", tag: "身份起底", heat: "764万", delta: "+28.1%", up: true },
  { rank: 3, title: "旧帖被翻出：涉事博主此前言论再被复盘", tag: "旧料翻出", heat: "641万", delta: "+21.4%", up: true },
  { rank: 4, title: "关联人物浮出水面，多方回应口径不一", tag: "关联人物", heat: "558万", delta: "+17.9%", up: true },
  { rank: 5, title: "背景深扒：当事机构近年公开动态被串起", tag: "背景深扒", heat: "472万", delta: "+12.3%", up: true },
  { rank: 6, title: "社交账号内容检索热度回落，讨论转向后续", tag: "账号查找", heat: "386万", delta: "-4.2%", up: false },
  { rank: 7, title: "澄清声明后评论区风向出现分化", tag: "进展动向", heat: "299万", delta: "-8.7%", up: false },
];

export const topicGroups: TopicGroup[] = [
  {
    id: "who",
    bucket: "当事人是谁",
    blurb: "从首曝线索反推公开身份，厘清被点名对象与代称之间的对应关系，回答「当事人到底是谁」。",
    tags: ["身份核实", "姓名疑云", "单位职业线", "公开履历", "首曝溯源", "代称对应"],
  },
  {
    id: "id",
    bucket: "身份起底",
    blurb: "基于公开信息还原人物画像，区分事实与猜测，呈现能查证到的身份拼图而非情绪化标签。",
    tags: ["履历公开", "就职轨迹", "地域背景", "教育脉络", "任职变动", "查证边界"],
  },
  {
    id: "bg",
    bucket: "背景深扒",
    blurb: "把事件放进更长的时间轴，串起关键前史与节点，让「突然爆红」背后有章可循。",
    tags: ["事件前史", "关键节点", "涉事机构", "时间轴梳理", "深层动因", "历史回应"],
  },
  {
    id: "rel",
    bucket: "关联人物",
    blurb: "梳理同框、交集与回应方，画出一张可读的关联关系网，避免只见树木不见林。",
    tags: ["关系网络", "同框交集", "圈层脉络", "相关方回应", "承接账号", "跟进媒体"],
  },
  {
    id: "acc",
    bucket: "社交账号查找",
    blurb: "只追踪公开可见的社交动态与帖子线索，定位讨论源头与传播路径，标注信息可验证程度。",
    tags: ["公开动态", "帖子溯源", "影像检索", "转载路径", "讨论源头", "信息验证"],
  },
  {
    id: "old",
    bucket: "旧料翻出",
    blurb: "按时间倒序翻阅可查证的历史言论与报道，区分「旧料」与「断章」，还原语境再下判断。",
    tags: ["历史帖文", "报道档案", "语境还原", "倒序翻查", "断章识别", "旧闻核对"],
  },
];

export const features: FeatureItem[] = [
  { icon: "ri-user-search-line", title: "网曝当事人是谁", desc: "面向高频追问，聚合多源公开线索，给出可复核的当事人身份速览。", kpi: "覆盖 1.2万 起事件", accent: true },
  { icon: "ri-fingerprint-line", title: "网曝身份起底", desc: "按事实层级拆分履历、职务与地域等公开信息，把画像拆成可打勾的条目。", kpi: "事实条目 63万+" },
  { icon: "ri-history-line", title: "网曝背景深扒", desc: "回到事件发生前的更长时间线，梳理前史与节点，帮你看清爆发路径。", kpi: "时间轴 8.9万 条" },
  { icon: "ri-git-branch-line", title: "网曝关联人物", desc: "把同框、交集与回应方拼成关系网络，厘清事件中每个人的位置。", kpi: "关系节点 4.7万" },
  { icon: "ri-shield-check-line", title: "网曝社交账号查找", desc: "仅整理公开可见的动态与帖子线索，标注来源与可验证程度，不碰隐私。", kpi: "线索索引 2.3万" },
  { icon: "ri-archive-drawer-line", title: "网曝旧料翻出", desc: "按时间倒序翻查可证历史言论与报道，补语境、辨断章，避免误读旧料。", kpi: "档案 15万 份" },
];

export const archives: ArchiveItem[] = [
  {
    id: "WB-2409",
    title: "点名举报类热曝：身份线索如何被一步步厘清",
    lead: "从一张公开截图开始，代称与实名如何对应，网络为何分成两派，档案复盘信息扩散路径。",
    meta: "热度飙升 · 事件复盘",
    heat: "982万",
    status: "持续发酵",
    img: "https://readdy.ai/api/search-image?query=editorial%20news%20collage%20of%20a%20laptop%20screen%20with%20text%20highlights%20and%20search%20magnifier%2C%20vermilion%20and%20charcoal%20ink%20tones%2C%20soft%20paper%20grain%2C%20abstract%20no%20real%20faces%2C%20cinematic%20studio%20lighting%2C%20clean%20graphic%20composition&width=800&height=1000&seq=archive-01&orientation=portrait",
  },
  {
    id: "WB-2411",
    title: "职场圈被点名者：履历核实与公开职务线",
    lead: "围绕公开履历、任职机构与过往报道，逐条标注「能查证」与「待核实」，还原背景深扒全过程。",
    meta: "身份起底 · 高关注",
    heat: "764万",
    status: "档案更新",
    img: "https://readdy.ai/api/search-image?query=minimalist%20office%20desk%20with%20folders%20documents%20and%20a%20laptop%2C%20warm%20desk%20lamp%2C%20editorial%20investigative%20research%20atmosphere%2C%20paper%20files%20and%20timeline%20printouts%2C%20no%20people%2C%20soft%20cinematic%20light&width=800&height=1000&seq=archive-02&orientation=portrait",
  },
  {
    id: "WB-2405",
    title: "旧帖被翻出的经典样本：语境与断章之争",
    lead: "同一段历史言论为何两种解读，翻出旧料后当事人补了哪句说明，样本逐帧还原上下文。",
    meta: "旧料翻出 · 辟谣复盘",
    heat: "641万",
    status: "已复盘",
    img: "https://readdy.ai/api/search-image?query=stack%20of%20old%20newspapers%20and%20typed%20documents%20on%20a%20warm%20wood%20desk%2C%20investigative%20archival%20research%2C%20vermilion%20tape%20marks%2C%20no%20people%2C%20soft%20side%20light%2C%20editorial%20still%20life%20photography&width=800&height=1000&seq=archive-03&orientation=portrait",
  },
  {
    id: "WB-2501",
    title: "多方回应口径不一：关联人物如何各说各话",
    lead: "当事机构、前关联人与跟进媒体先后发声，整理同框交集，画出一张可读的关系回应时间线。",
    meta: "关联人物 · 网络梳理",
    heat: "558万",
    status: "持续发酵",
    img: "https://readdy.ai/api/search-image?query=abstract%20network%20diagram%20of%20connected%20nodes%20and%20lines%20projected%20on%20a%20dark%20wall%2C%20vermilion%20and%20amber%20threads%20on%20charcoal%20background%2C%20no%20faces%2C%20editorial%20data%20visualization%2C%20dramatic%20lighting&width=800&height=1000&seq=archive-04&orientation=portrait",
  },
  {
    id: "WB-2308",
    title: "长线热曝：从爆发到澄清的完整时间轴",
    lead: "把两个月里的关键节点串成一根线，还原爆发、跟进、澄清到回落的完整背景深扒进程。",
    meta: "背景深扒 · 长篇复盘",
    heat: "472万",
    status: "已归档",
    img: "https://readdy.ai/api/search-image?query=timeline%20infographic%20printed%20on%20paper%20rolls%20with%20photo%20and%20sticky%20notes%20on%20a%20research%20board%2C%20vermilion%20and%20warm%20beige%20palette%2C%20editorial%20documentary%2C%20no%20people%2C%20soft%20evening%20lighting&width=800&height=1000&seq=archive-05&orientation=portrait",
  },
  {
    id: "WB-2503",
    title: "公开动态检索视角：讨论热度为何此起彼伏",
    lead: "观察公开社交动态与转载路径的起伏节奏，拆解话题从冲到缓的几次波峰与关键转折点。",
    meta: "账号查找 · 传播观察",
    heat: "386万",
    status: "观察中",
    img: "https://readdy.ai/api/search-image?query=rising%20bar%20chart%20on%20a%20glowing%20monitor%20with%20search%20filters%20and%20magnifier%2C%20warm%20amber%20and%20charcoal%20tones%2C%20no%20faces%2C%20editorial%20analytics%20scene%2C%20soft%20cinematic%20glow&width=800&height=1000&seq=archive-06&orientation=portrait",
  },
];

export const timelineSteps: TimelineStep[] = [
  { time: "爆发前 · 前史", title: "首条公开线索出现", desc: "一张公开截图与一段发言被转发，讨论从某小众社群蔓延至公开时间线。", type: "前史" },
  { time: "T+0", title: "当事人是谁成为最高频追问", desc: "代称与实名如何在讨论中对上号，首个「身份疑云」话题迅速登顶热度榜。", type: "当事人是谁" },
  { time: "T+1", title: "身份起底与背景深扒同时展开", desc: "公开履历、任职机构与过往报道被逐条梳理，能查证与待核实被分开标注。", type: "身份起底" },
  { time: "T+2", title: "关联人物与旧帖被接连翻出", desc: "同框交集浮现、多方回应口径不一，一段历史言论在「翻出」后被补充语境。", type: "关联人物" },
  { time: "T+3", title: "澄清声明发布，热度走向分化", desc: "官方与个人先后回应，评论区转向后续追问，讨论节奏明显放缓但仍有余温。", type: "进展动向" },
  { time: "T+7", title: "复盘与档案归档", desc: "完整时间轴被串起并归档，供后来者快速了解全貌，避免重复误解旧料。", type: "复盘" },
];

export const relationNodes: RelationNode[] = [
  { role: "被点名对象", name: "事件当事人 A", note: "已两次公开回应，口径随进展调整。", letter: "A" },
  { role: "涉事机构", name: "单位组织 B", note: "声明称正核实，暂未给出时间表。", letter: "B" },
  { role: "首曝来源", name: "线索提供方 C", note: "首条公开截图由该方向转发扩散。", letter: "C" },
  { role: "前关联方", name: "历史关联者 D", note: "与当事人在早期公开交集被翻出。", letter: "D" },
  { role: "旁证账号", name: "跟进搬运号 E", note: "集中转载与二次解读，加速传播。", letter: "E" },
  { role: "跟进媒体", name: "多家媒体 F", note: "已发出核实报道，立场审慎。", letter: "F" },
];

export const footColumns = [
  {
    title: "追踪模块",
    links: ["热度榜", "话题标签墙", "事件档案", "进展时间线", "关联人物网络", "复盘归档"],
  },
  {
    title: "内容栏目",
    links: ["网曝当事人是谁", "网曝身份起底", "网曝背景深扒", "网曝关联人物", "网曝社交账号查找", "网曝旧料翻出"],
  },
  {
    title: "关于本站",
    links: ["站内说明", "信息来源说明", "事实核查边界", "免责声明", "内容规范", "联系我们"],
  },
];

export const footCerts = [
  "仅整理公开可见信息",
  "标注可查证程度",
  "区分事实与推断",
  "尊重核实与撤回",
];